/**
 * Toast + Confirm 状态管理
 *
 * 用法：
 *   import { toast, confirm } from '../store/toastStore';
 *
 *   toast.success('保存成功');               // 成功通知
 *   toast.error('操作失败');                   // 错误通知
 *   toast.warning('请先填写信息');             // 表单验证
 *   toast.info('已取消编辑');                  // 一般提示
 *
 *   if (!await confirm({ title:'确认', message:'确定删除吗？', confirmLabel:'删除', danger:true })) return;
 */

import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration: number;
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}
interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
  resolve: ((value: boolean) => void) | null;
}

interface ToastStore {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id' | 'duration'> & { duration?: number }) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  confirmState: ConfirmState;
  showConfirm: (opts: ConfirmOptions) => Promise<boolean>;
  _resolveConfirm: (value: boolean) => void;
  _resetConfirm: () => void;
}

let toastCounter = 0;

const DEFAULT_CONFIRM: ConfirmState = {
  isOpen: false,
  title: '确认',
  message: '',
  confirmLabel: '确认',
  cancelLabel: '取消',
  danger: false,
  resolve: null,
};

export const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],

  addToast: (t) => {
    const id = `toast-${++toastCounter}`;
    const duration = t.duration ?? 3500;
    set((s) => ({ toasts: [...s.toasts, { ...t, id, duration }] }));
    if (duration > 0) {
      setTimeout(() => get().removeToast(id), duration);
    }
  },

  removeToast: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },

  clearToasts: () => set({ toasts: [] }),

  confirmState: { ...DEFAULT_CONFIRM },

  showConfirm: (opts) =>
    new Promise<boolean>((resolve) => {
      set({
        confirmState: {
          isOpen: true,
          title: opts.title ?? '确认',
          message: opts.message,
          confirmLabel: opts.confirmLabel ?? '确认',
          cancelLabel: opts.cancelLabel ?? '取消',
          danger: opts.danger ?? false,
          resolve,
        },
      });
    }),

  _resolveConfirm: (value) => {
    const state = get().confirmState;
    state.resolve?.(value);
    get()._resetConfirm();
  },

  _resetConfirm: () => set({ confirmState: { ...DEFAULT_CONFIRM } }),
}));

/* ── 便捷函数（可在任何地方直接调用，无需 React Hook） ── */

export const toast = {
  success: (message: string, title?: string) =>
    useToastStore.getState().addToast({ type: 'success', message, title }),
  error: (message: string, title?: string) =>
    useToastStore.getState().addToast({ type: 'error', message, title }),
  info: (message: string, title?: string) =>
    useToastStore.getState().addToast({ type: 'info', message, title }),
  warning: (message: string, title?: string) =>
    useToastStore.getState().addToast({ type: 'warning', message, title }),
};

export const confirm = (opts: ConfirmOptions) =>
  useToastStore.getState().showConfirm(opts);
